/**
 * Lazy-loaded responsive AdSense slot.
 * Only renders if window.adsbygoogle is available (which the index.html
 * lazy-loader sets up after first user interaction). Safe to use anywhere.
 *
 * Usage:
 *   <AdSlot slot="1234567890" />              // default in-content
 *   <AdSlot slot="1234567890" format="auto"/> // auto-sized
 */
import { useEffect, useRef } from "react";

const PUBLISHER_ID = "ca-pub-4714137890809318";

export default function AdSlot({ slot, format = "auto", layout, className = "" }) {
  const ref = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (!slot) return;
    if (pushed.current) return;
    const tryPush = () => {
      if (window.adsbygoogle && ref.current) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          pushed.current = true;
        } catch (e) {
          // adsbygoogle not loaded yet — retry once on next idle
        }
      }
    };
    tryPush();
    const t = setTimeout(tryPush, 1500);
    const t2 = setTimeout(tryPush, 5000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [slot]);

  // Render nothing if no slot ID is configured (yet) — avoids empty ad units in the DOM
  // until the user supplies real slot IDs from the AdSense dashboard.
  if (!slot) return null;

  return (
    <div className={`ad-slot my-6 text-center ${className}`} data-testid={`ad-slot-${slot}`}>
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: "block", minHeight: 100 }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        {...(layout ? { "data-ad-layout": layout } : {})}
        data-full-width-responsive="true"
      ></ins>
    </div>
  );
}
